import { AxiosError } from 'axios';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Rpc } from '@/models/Rpc';

/**
 * Maia — AI styling coach chat widget (CLD-13).
 *
 * A standalone, reusable overlay panel (briefing 5.2). It owns no entry point of
 * its own by default: the host decides what opens it and passes session context
 * in as props, so the same component can be embedded on any Sara screen without
 * a page reload. Control the panel with `v-model`.
 *
 * Talks to the `maia:chat` procedure. There is no server-side conversation store
 * yet, so this component owns the transcript and replays it with every message —
 * the backend caps and sanitises what it accepts.
 *
 * Product cards (briefing 5.4) come from the `products` key on the response —
 * only the items Maia actually cited, not everything she searched. Clicking one
 * opens the shop's own product view in the main window, and the conversation
 * survives it — see onProductClick.
 *
 * Not built yet: streaming. A reply arrives in one piece.
 */
@Component<MaiaChat>({})
export default class MaiaChat extends Vue {
  // #region @Props

  // `type` is spelled out on every prop below: this project sets
  // experimentalDecorators without emitDecoratorMetadata and has no
  // reflect-metadata, so vue-property-decorator cannot infer a prop's type from
  // its TS annotation. Without an explicit Boolean type Vue never coerces a bare
  // `show-launcher` attribute to true — it stays the empty string, which is falsy.

  /** Open state of the panel. Use with `v-model` so the host owns the trigger. */
  @Prop({ type: Boolean, default: false })
  protected value!: boolean;

  /**
   * Render the chat icon. Off by default so a host that already has its own
   * trigger does not end up with two.
   */
  @Prop({ type: Boolean, default: false })
  protected showLauncher!: boolean;

  /** Which corner the panel opens from. Point it at whatever icon opens the chat. */
  @Prop({ type: String, default: 'right' })
  protected anchor!: MaiaAnchor;

  /** The order/box the stylist is currently composing. Passed to Maia as session context. */
  @Prop({ type: String, default: '' })
  protected orderId!: string;

  /** The customer the stylist is shopping for. */
  @Prop({ type: String, default: '' })
  protected customerName!: string;

  // #endregion

  // #region @Refs
  // #endregion

  // #region Class properties

  protected isTyping: boolean = false;

  protected draft: string = '';

  protected messages: MaiaMessage[] = [];

  private nextMessageId: number = 1;

  /**
   * Identifies this conversation to the backend so its turns can be read back
   * together (briefing 7, logging & review). There is no server-side
   * conversation store, so the id has to come from here — the same place the
   * transcript lives.
   */
  private conversationId: string = '';

  // #endregion

  // #region Lifecycle Hooks / Init

  public mounted(): void {
    this.restoreConversation();
    this.ensureConversationId();

    if (this.value) {
      this.seedConversation();
    }
  }

  // #endregion

  // #region Handlers

  protected onToggleButtonClick(): void {
    this.$emit('input', !this.value);
  }

  protected onCloseButtonClick(): void {
    this.$emit('input', false);
  }

  /**
   * Open the product in the shop's main window (briefing 5.4).
   *
   * Same tab, same route the shop's own product grid links to — "niet in een
   * nieuw tabblad, niet binnen de chat zelf". The panel is deliberately NOT
   * closed and the conversation is not lost, because this component is mounted
   * in App above the router-view rather than inside the webshop view, so the
   * navigation does not unmount it.
   */
  protected onProductClick(product: MaiaProduct): void {
    if (!this.orderId || !product.id) {
      return;
    }

    this.$router.push(`/webshop/order/${this.orderId}/product/${product.id}`);
  }

  /**
   * A product image that will not load is worse than none.
   *
   * The url is a permanent CloudFront one, but the catalogue is old enough
   * that some of them point at files that are gone. Dropping the element on
   * error leaves the same placeholder a product with no photo gets, rather
   * than a broken-image icon in the middle of a recommendation.
   */
  protected onProductImageError(event: Event): void {
    const image = event.target as HTMLImageElement | null;

    if (image && image.parentElement) {
      image.parentElement.removeChild(image);
    }
  }

  protected onSuggestionClick(suggestion: string): void {
    this.draft = suggestion;
    this.onSendButtonClick();
  }

  protected onSendButtonClick(): void {
    const text = this.draft.trim();

    if (!text || this.isTyping) {
      return;
    }

    // Read the history before the question goes in, so the message being asked
    // is not also replayed as part of the conversation so far.
    const history = this.conversationHistory;

    this.addMessage({ author: 'stylist', text });
    this.draft = '';
    // Fire and forget: askMaia owns both outcomes, so there is nothing here to
    // handle. It returns its promise so a test can wait for the round trip.
    this.askMaia(text, history);
  }

  // #endregion

  // #region Helpers

  /** Drops in the opening message the first time the panel is opened. */
  private seedConversation(): void {
    if (this.messages.length) {
      this.scrollToBottom();

      return;
    }

    this.addMessage({
      author: 'maia',
      text: this.customerName
        ? this.$tc('orders.orderDetail.webshop.maia.greetingNamed', 1, { customer: this.customerName })
        : this.$tc('orders.orderDetail.webshop.maia.greeting'),
    });
  }

  private addMessage(message: Omit<MaiaMessage, 'id'>): void {
    this.messages.push({ ...message, id: this.nextMessageId++ });
    this.persistConversation();
    this.scrollToBottom();
  }

  /**
   * Where the transcript is kept between page loads.
   *
   * The panel now lives above the router-view, so an in-app navigation no longer
   * costs the conversation and this is a backstop rather than the mechanism. It
   * still earns its place: a hard refresh, or leaving Sara and coming back, would
   * otherwise start the stylist over from nothing.
   *
   * Keyed per order because the transcript is about one customer's box. Session
   * storage rather than local: it goes when the tab does, which is the right
   * lifetime for advice about a specific customer.
   */
  private get storageKey(): string {
    return `maia:transcript:${this.orderId}`;
  }

  private get conversationKey(): string {
    return `maia:conversation:${this.orderId}`;
  }

  /**
   * Reuse the conversation id across page loads, mint one otherwise.
   *
   * It has to survive the same trip the transcript does: clicking a product card
   * navigates away and unmounts this panel, and the turns before and after that
   * are still one conversation to whoever reviews it later.
   */
  private ensureConversationId(): void {
    if (!this.orderId) {
      this.conversationId = MaiaChat.newUuid();

      return;
    }

    try {
      const stored = window.sessionStorage.getItem(this.conversationKey);

      if (stored) {
        this.conversationId = stored;

        return;
      }

      this.conversationId = MaiaChat.newUuid();
      window.sessionStorage.setItem(this.conversationKey, this.conversationId);
    } catch (error) {
      // Private browsing, or a full quota. An id that lives only in memory still
      // groups this page load's turns, which is most of the value.
      this.conversationId = MaiaChat.newUuid();
    }
  }

  /**
   * A v4 uuid.
   *
   * crypto.randomUUID would be tidier but needs a secure context, and Sara is
   * served over plain http in development — so it is used when available and
   * built by hand when not. The backend validates the shape.
   */
  private static newUuid(): string {
    const cryptoApi = window.crypto as Crypto & { randomUUID?: () => string };

    if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
      return cryptoApi.randomUUID();
    }

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (character: string) => {
      const random = Math.random() * 16 | 0;
      const value = character === 'x' ? random : ((random & 0x3) | 0x8);

      return value.toString(16);
    });
  }

  private restoreConversation(): void {
    if (!this.orderId) {
      return;
    }

    try {
      const stored = window.sessionStorage.getItem(this.storageKey);

      if (!stored) {
        return;
      }

      const messages = JSON.parse(stored) as MaiaMessage[];

      if (!Array.isArray(messages) || !messages.length) {
        return;
      }

      this.messages = messages;
      // Carry on numbering above whatever was restored, so keys stay unique.
      this.nextMessageId = Math.max(...messages.map((message: MaiaMessage) => message.id)) + 1;
    } catch (error) {
      // A malformed or unreadable store is not worth failing the panel over —
      // she gets a fresh conversation, which is what she had before this existed.
      window.sessionStorage.removeItem(this.storageKey);
    }
  }

  private persistConversation(): void {
    if (!this.orderId) {
      return;
    }

    try {
      window.sessionStorage.setItem(this.storageKey, JSON.stringify(this.messages));
    } catch (error) {
      // Quota, or private browsing. The conversation still works in memory.
    }
  }

  /**
   * The rules that were actually judged for this product.
   *
   * Maarten's second report was that no full crosscheck against the profile
   * seemed to happen. It does now, server-side and deterministically, and this
   * is where the stylist gets to see it — so she can tell a suggestion that
   * was checked from one that only looks like it was.
   */
  protected verifiedChecks(product: MaiaProduct): MaiaProductCheck[] {
    return (product.checks || []).filter((check: MaiaProductCheck) => check.status !== 'unknown');
  }

  /** The rules the catalogue cannot answer, as one readable list. */
  protected uncheckedRules(product: MaiaProduct): string {
    return (product.checks || [])
      .filter((check: MaiaProductCheck) => check.status === 'unknown')
      .map((check: MaiaProductCheck) => this.ruleLabel(check.rule))
      .join(', ');
  }

  protected checkText(check: MaiaProductCheck): string {
    const rule = this.ruleLabel(check.rule);

    if (check.status === 'warn' && check.detail) {
      return this.$tc('orders.orderDetail.webshop.maia.checks.over', 1, { amount: check.detail });
    }

    if (check.status === 'fail') {
      return this.$tc('orders.orderDetail.webshop.maia.checks.breaks', 1, { rule });
    }

    return rule;
  }

  protected ruleLabel(rule: string): string {
    return this.$tc(`orders.orderDetail.webshop.maia.checks.rules.${rule}`);
  }

  private scrollToBottom(): void {
    this.$nextTick(() => {
      const list = this.$refs.messageList as HTMLElement | undefined;

      if (!list) {
        return;
      }

      list.scrollTop = list.scrollHeight;
    });
  }

  // #endregion

  // #region Async methods

  /**
   * Ask the backend and append the answer.
   *
   * Failures land in the panel as a message rather than in a modal: the stylist
   * is mid-conversation, and a dialog over the chat would be a bigger
   * interruption than the failure itself. The error bubble is kept out of the
   * history so a retry does not carry our own error text back to the model.
   */
  private askMaia(message: string, history: MaiaTurn[]): Promise<void> {
    this.isTyping = true;

    return new Rpc()
      .execute('maia:chat', {
        message,
        language: this.language,
        order_id: this.orderId || undefined,
        conversation_id: this.conversationId || undefined,
        history,
      })
      .then((response: MaiaChatResponse) => {
        this.addMessage({
          author: 'maia',
          text: response.reply,
          products: response.products && response.products.length ? response.products : undefined,
        });
      })
      .catch((error: AxiosError) => {
        // eslint-disable-next-line no-console
        console.error('[Maia] chat request failed', error);
        this.addMessage({
          author: 'maia',
          text: this.$tc('orders.orderDetail.webshop.maia.error'),
          isError: true,
        });
      })
      .finally(() => {
        this.isTyping = false;
      });
  }

  // #endregion

  // #region Getters & Setters

  protected get isOpen(): boolean {
    return this.value;
  }

  /**
   * The language the stylist picked in Sara's dropdown (briefing 5.1).
   *
   * `LanguageSwitch` writes that choice to `user.language` and `App` feeds it
   * into i18n on boot, so the app locale IS the stylist's setting — the widget
   * just reads it rather than keeping a preference of its own.
   *
   * This value has to travel to the backend 1-on-1 alongside klant_id and
   * stylist_id (briefing 7): the customer's quiz profile is always filled in in
   * Dutch, so it is the system prompt — not the data — that decides which
   * language Maia answers in.
   */
  protected get language(): string {
    return this.$i18n.locale;
  }

  /**
   * The conversation so far, in the shape the backend accepts.
   *
   * The opening greeting goes along even though we wrote it rather than the
   * model: it is what the stylist actually saw, and it is what stops Maia from
   * introducing herself a second time (briefing 5.5, "één introductie per
   * gesprek"). Error bubbles are ours and are left out.
   */
  protected get conversationHistory(): MaiaTurn[] {
    return this.messages
      .filter((message: MaiaMessage) => !message.isError)
      .map((message: MaiaMessage) => ({
        role: message.author === 'maia' ? 'assistant' : 'user',
        content: message.text,
      })) as MaiaTurn[];
  }

  /**
   * Starter questions from the briefing. A getter, not a field, so they follow
   * the stylist along when she switches language mid-session.
   */
  protected get suggestions(): string[] {
    return [
      this.$tc('orders.orderDetail.webshop.maia.suggestions.jeans'),
      this.$tc('orders.orderDetail.webshop.maia.suggestions.eyeCatcher'),
      this.$tc('orders.orderDetail.webshop.maia.suggestions.missing'),
    ];
  }

  protected get canSend(): boolean {
    return !!this.draft.trim() && !this.isTyping;
  }

  // #endregion

  // #region @Watchers

  @Watch('value')
  protected valueChanged(isOpen: boolean): void {
    if (!isOpen) {
      return;
    }

    this.seedConversation();
  }

  // #endregion
}

// #region Enums
// #endregion

// #region Types

export type MaiaAuthor = 'maia' | 'stylist';

export type MaiaAnchor = 'left' | 'right';

/** The roles `maia:chat` accepts in its history parameter. */
export type MaiaTurnRole = 'user' | 'assistant';

// #endregion

// #region Interfaces

/**
 * A product Maia actually recommended (briefing 5.4).
 *
 * `id` is the product-variant uuid, which is what the shop's own product links
 * route on — see the grid in Webshop.vue.
 */
export interface MaiaProduct {
  id: string;
  brand: string;
  name: string;
  colour: string;
  size: string;
  price: number | null;
  stock: number;
  /**
   * The product photo, or null. Roughly a fifth of sellable stock has none, so
   * the card has to hold its shape without one. It is a product-level image, so
   * a colour variant may show another colour's photo.
   */
  imageUrl: string | null;
  /** What the backend made of this item against the customer's profile. */
  checks?: MaiaProductCheck[];
}

/**
 * One rule, and how this product fared against it.
 *
 * Keys rather than sentences, because the stylist reads them in her own
 * language and the backend has no business choosing it — see
 * App\Services\Maia\MaiaRecommendationCheck for what each status means.
 */
export interface MaiaProductCheck {
  rule: string;
  status: MaiaCheckStatus;
  detail?: string;
}

export type MaiaCheckStatus = 'pass' | 'fail' | 'warn' | 'unknown';

export interface MaiaMessage {
  id: number;
  author: MaiaAuthor;
  text: string;
  products?: MaiaProduct[];
  /** Our own "something went wrong" bubble, not something Maia said. */
  isError?: boolean;
}

/** One previous turn, as `maia:chat` expects it. */
export interface MaiaTurn {
  role: MaiaTurnRole;
  content: string;
}

export interface MaiaChatResponse {
  reply: string;
  /** The language Maia actually answered in, after any fallback. */
  language: string;
  /** Only the products she named, in the order she named them. Often empty. */
  products?: MaiaProduct[];
}

// #endregion
